// src/api/commentaires.api.ts — SCE Issues
// CRUD des commentaires / événements de timeline dans SharePoint SCE_Commentaires
//
// ⚠️ Mapping colonnes SharePoint :
//   Title     → ID custom format "P2026-0042-C01"
//   field_1   → ProblemId (ex: "P2026-0042")
//   field_3   → Date (ISO 8601)
//   field_4   → TypeAction (choice)
//   field_5   → Contenu
//   field_6   → StatutAvant (choice)
//   field_7   → StatutApres (choice)
//
// ⚠️ field_6 et field_7 sont des colonnes Choice : elles doivent contenir
//    TOUS les statuts, y compris `Annulé` depuis le Lot 2. Sans ça, l'événement
//    d'annulation est rejeté à l'écriture — l'annulation elle-même aboutit,
//    mais la timeline n'en garde aucune trace.
//
// ⚠️ Plus de colonne Auteur dédiée : on utilise la colonne SYSTÈME `Author`
//   de SharePoint (= "Créé par"). Marche pour internes ET guests B2B.
//
// ─── Note importante sur la syntaxe Graph ───
//
// Pour récupérer createdBy : $expand=fields&$select=id,createdDateTime,createdBy
// (PAS $expand=fields,createdBy → 400 Bad Request).

import { graphGet, graphPost, graphPatch } from './graphClient'
import type { Commentaire, CommentaireType, Statut } from '@/types/issue.types'
import { nomComplet } from '@/utils/noms'
import { auteurCanonique } from '@/config/comptesAlias.config'
import {
  dateDeCorrection,
  erreurCorrection,
  peutModifierCommentaire,
  versionsDuCommentaire,
  type VersionBrute,
  type VersionCommentaire,
} from '@/utils/editionCommentaire'
import { colonneSaisiPar } from '@/auth/comptePartage'

// ─── Configuration SharePoint ───

const SITE_ID = import.meta.env.VITE_SHAREPOINT_SITE_ID ?? ''
const LIST_ID = import.meta.env.VITE_LIST_COMMENTAIRES_ID ?? ''

function listPath(): string {
  return `/sites/${SITE_ID}/lists/${LIST_ID}`
}

// lastModifiedDateTime : un commentaire corrigé par son auteur (23/10).
const ITEM_SELECT = '$expand=fields&$select=id,createdDateTime,lastModifiedDateTime,createdBy'

// ─── Format de l'ID custom ───

function buildCommentaireId(problemId: string, counter: number): string {
  const pad = String(counter).padStart(2, '0')
  return `${problemId}-C${pad}`
}

async function getLastCounterForProblem(problemId: string): Promise<number> {
  const prefix = `${problemId}-C`
  const filter = `startswith(fields/Title,'${escapeODataString(prefix)}')`
  const path =
    `${listPath()}/items?$expand=fields($select=Title)` +
    `&$filter=${encodeURIComponent(filter)}` +
    `&$orderby=fields/Title desc&$top=1`

  const response = await graphGet<SharePointListResponse<{ Title?: string }>>(path)
  const lastTitle = response.value[0]?.fields.Title
  if (!lastTitle) return 0

  const match = lastTitle.match(/-C(\d+)$/)
  if (!match) return 0
  return parseInt(match[1] ?? '0', 10)
}

// ─── Structure brute Graph API ───

interface CommentaireFields {
  Title?: string
  field_1?: string
  field_3?: string
  field_4?: string
  field_5?: string
  field_6?: Statut
  field_7?: Statut
  /** UPN de qui tenait l'appareil partagé (07/10), cf. auth/comptePartage. */
  SaisiPar?: string
}

interface GraphIdentity {
  user?: {
    displayName?: string
    email?: string
    id?: string
  }
}

interface SharePointListItem<T> {
  id: string
  createdDateTime?: string
  lastModifiedDateTime?: string
  fields: T & { Title?: string }
  createdBy?: GraphIdentity
}

interface SharePointListResponse<T> {
  value: SharePointListItem<T>[]
}

// ─── Mapping TypeAction ↔ libellé SharePoint ───

//
// ⚠️ PRÉ-REQUIS SHAREPOINT : chaque libellé de TYPE_TO_SP_LABEL doit exister
//    À L'IDENTIQUE dans la liste de choix de SCE_Commentaires/field_4, sinon
//    l'écriture de l'événement est rejetée. `Annulation` est nouveau (Lot 2).

const TYPE_TO_SP_LABEL: Record<CommentaireType, string> = {
  Creation: 'Création',
  Commentaire: 'Commentaire',
  ChangementStatut: 'Changement statut',
  Cloture: 'Clôture',
  Qualification: 'Qualification',
  Annulation: 'Annulation',
  // Lot 3 bis : référence qualité renseignée après la clôture. Même libellé
  // que celui prévu au cadrage pour l'édition des coordinateurs (Lot 6).
  Modification: 'Modification',
}

/**
 * Libellé SharePoint → type applicatif, pour la RELECTURE.
 *
 * Construite depuis TYPE_TO_SP_LABEL plutôt qu'écrite à la main : c'était une
 * copie séparée, et elle avait oublié `Qualification`. Conséquence réelle
 * jusqu'au Lot 2 : chaque qualification était ÉCRITE correctement mais
 * RELUE comme un 'Commentaire', et s'affichait dans la timeline avec l'icône
 * et la couleur d'un commentaire banal. Dériver les deux sens d'une seule
 * table rend cet oubli impossible.
 *
 * Les graphies sans accent restent acceptées en relecture : des événements
 * anciens ont été écrits sous cette forme.
 */
const SP_LABEL_TO_TYPE: Record<string, CommentaireType> = {
  ...Object.fromEntries(
    (Object.entries(TYPE_TO_SP_LABEL) as [CommentaireType, string][]).map(
      ([type, libelle]) => [libelle, type],
    ),
  ),
  // Graphies historiques
  'Creation': 'Creation',
  'ChangementStatut': 'ChangementStatut',
  'Cloture': 'Cloture',
}

function decodeType(raw: string | undefined): CommentaireType {
  if (!raw) return 'Commentaire'
  return SP_LABEL_TO_TYPE[raw] ?? 'Commentaire'
}

// ─── Mapper ───

function mapItemToCommentaire(
  item: SharePointListItem<CommentaireFields>,
): Commentaire {
  const f = item.fields
  // Compte secondaire ramené à la personne (config/comptesAlias.config) ;
  // appareil partagé : la colonne SaisiPar dit qui le tenait.
  const auteur = auteurCanonique(item.createdBy?.user, undefined, undefined, f.SaisiPar)

  const c: Commentaire = {
    id: f.Title ?? `#${item.id}`,
    problemId: f.field_1 ?? '',
    type: decodeType(f.field_4),
    auteur: nomComplet(auteur?.displayName ?? auteur?.email ?? '', auteur?.email),
    date: f.field_3 ?? item.createdDateTime ?? '',
    contenu: f.field_5 ?? '',
  }
  if (f.field_6) c.ancienStatut = f.field_6
  if (f.field_7) c.nouveauStatut = f.field_7
  c.itemId = item.id
  if (auteur?.email) c.auteurEmail = auteur.email.trim().toLowerCase()
  const corrige = dateDeCorrection(item.createdDateTime, item.lastModifiedDateTime)
  if (corrige) c.modifieLe = corrige
  return c
}

function escapeODataString(value: string): string {
  return value.replace(/'/g, "''")
}

// ─── API publique ───

export async function getCommentaires(problemId: string): Promise<Commentaire[]> {
  const filter = `fields/field_1 eq '${escapeODataString(problemId)}'`
  const path =
    `${listPath()}/items?${ITEM_SELECT}` +
    `&$filter=${encodeURIComponent(filter)}` +
    `&$orderby=fields/field_3 asc&$top=200`

  const response = await graphGet<SharePointListResponse<CommentaireFields>>(path)
  return response.value.map(mapItemToCommentaire)
}

/**
 * Récupère tous les commentaires postés par un auteur donné (UC future
 * "activité d'une personne"). Filtre côté client sur le displayName.
 */
export async function getCommentairesByAuteur(
  nom: string,
): Promise<Commentaire[]> {
  const path =
    `${listPath()}/items?${ITEM_SELECT}` +
    `&$orderby=fields/field_3 desc&$top=200`

  const response = await graphGet<SharePointListResponse<CommentaireFields>>(path)
  const all = response.value.map(mapItemToCommentaire)
  return all.filter((c) => c.auteur.toLowerCase() === nom.toLowerCase())
}

// ─── Accueil (Lot 9) ───

/**
 * Les N derniers événements, tous problèmes confondus — le fil « Activité
 * récente » de l'accueil. Une requête, triée par SharePoint sur la date.
 *
 * Tri sur la colonne Date (`field_3`), indexée dans SCE_Commentaires (confirmé
 * le 25/09) : il tient au-delà de 5 000 éléments.
 */
export async function getActiviteRecente(n = 50): Promise<Commentaire[]> {
  const path =
    `${listPath()}/items?${ITEM_SELECT}` +
    `&$orderby=fields/field_3 desc&$top=${n}`
  const response = await graphGet<SharePointListResponse<CommentaireFields>>(path)
  return response.value.map(mapItemToCommentaire)
}

/**
 * L'identifiant du DERNIER événement (03/11) : une ligne, une colonne, sur la
 * Date indexée. Lu toutes les minutes par la veille des changements
 * (features/rafraichissement) : s'il change, quelqu'un a agi, les listes se
 * relisent. null si la liste est vide.
 */
export async function dernierEvenement(): Promise<string | null> {
  const path = `${listPath()}/items?$expand=fields($select=field_3)&$select=id&$orderby=fields/field_3 desc&$top=1`
  const response = await graphGet<SharePointListResponse<{ field_3?: string }>>(path)
  return response.value[0]?.id ?? null
}

/** Page d'une lecture paginée des événements complets. */
interface PageEvenements extends SharePointListResponse<CommentaireFields> {
  '@odata.nextLink'?: string
}

/**
 * Tous les événements postérieurs à `depuis` (ISO), les plus récents d'abord —
 * le récap « retour de vacances » (28/10). Le même tri que l'activité récente
 * (colonne Date indexée), lu page par page jusqu'à dépasser `depuis` ; plafonné
 * pour qu'une absence très longue ne lise pas toute la liste.
 */
export async function listerEvenementsDepuis(depuis: string, plafond = 3000): Promise<Commentaire[]> {
  const seuil = Date.parse(depuis)
  const res: Commentaire[] = []
  let chemin: string | undefined =
    `${listPath()}/items?${ITEM_SELECT}&$orderby=fields/field_3 desc&$top=250`
  while (chemin && res.length < plafond) {
    const page: PageEvenements = await graphGet<PageEvenements>(chemin)
    let fini = page.value.length === 0
    for (const item of page.value) {
      const c = mapItemToCommentaire(item)
      if (Date.parse(c.date) <= seuil) {
        fini = true
        break
      }
      res.push(c)
    }
    chemin = fini ? undefined : page['@odata.nextLink']
  }
  return res
}

export interface DateEvenement {
  problemId: string
  /** ISO 8601. */
  date: string
}

/** Page d'une lecture paginée : Graph renvoie la suite dans `@odata.nextLink`. */
interface PageDates extends SharePointListResponse<Pick<CommentaireFields, 'field_1' | 'field_3'>> {
  '@odata.nextLink'?: string
}

/**
 * La date de TOUS les événements, pour le compteur « Sans activité depuis
 * 7 jours » de l'accueil.
 *
 * Fiable à tout volume : ni filtre ni tri OData (les deux cassent au-delà de
 * 5 000 éléments sur une colonne non indexée), une simple lecture paginée de
 * deux colonnes — quelques dizaines d'octets par événement.
 */
export async function listerDatesEvenements(): Promise<DateEvenement[]> {
  const dates: DateEvenement[] = []
  let chemin: string | undefined =
    `${listPath()}/items?$expand=fields($select=field_1,field_3)` +
    `&$select=id,createdDateTime&$top=500`
  while (chemin) {
    const page: PageDates = await graphGet<PageDates>(chemin)
    for (const item of page.value) {
      const problemId = item.fields.field_1
      const date = item.fields.field_3 ?? item.createdDateTime
      if (problemId && date) dates.push({ problemId, date })
    }
    // URL absolue : graphGet la reconnaît et ne la préfixe pas.
    chemin = page['@odata.nextLink']
  }
  return dates
}

// ─── Payload de création ───

export interface NewCommentaireInput {
  problemId: string
  type: CommentaireType
  contenu: string
  ancienStatut?: Statut
  nouveauStatut?: Statut
}

/**
 * Ajoute un événement à la timeline d'un problème.
 *
 * Création en 2 temps :
 *   1. POST sans Title → SharePoint alimente Author automatiquement
 *   2. PATCH du Title avec le format "P2026-0042-C03"
 */
export async function addCommentaire(
  input: NewCommentaireInput,
): Promise<Commentaire> {
  const fields: CommentaireFields = {
    field_1: input.problemId,
    field_3: new Date().toISOString(),
    field_4: TYPE_TO_SP_LABEL[input.type],
    field_5: input.contenu,
  }
  if (input.ancienStatut) fields.field_6 = input.ancienStatut
  if (input.nouveauStatut) fields.field_7 = input.nouveauStatut
  Object.assign(fields, colonneSaisiPar())

  // 1. POST initial
  const created = await graphPost<SharePointListItem<CommentaireFields>>(
    `${listPath()}/items`,
    { fields },
  )

  // 2. Calcul de l'ID custom et PATCH du Title
  const lastCounter = await getLastCounterForProblem(input.problemId)
  const customId = buildCommentaireId(input.problemId, lastCounter + 1)

  await graphPatch<unknown>(
    `${listPath()}/items/${created.id}/fields`,
    { Title: customId },
  )

  // 3. Re-fetch avec la bonne syntaxe
  const full = await graphGet<SharePointListItem<CommentaireFields>>(
    `${listPath()}/items/${created.id}?${ITEM_SELECT}`,
  )
  return mapItemToCommentaire(full)
}

// ─── Correction par son auteur (23/10) ───

/**
 * Remplace le texte d'un commentaire, par son auteur seulement. La ligne est
 * RELUE avant d'écrire : type, auteur et texte vérifiés sur ce qui est stocké,
 * pas sur ce que la fiche affichait.
 *
 * @param avant le texte affiché quand la correction a commencé
 * @param identites celles de qui est connecté (identitesDeclarant)
 * @throws Error si ce n'est pas son commentaire, s'il a changé entre-temps,
 *         ou si le nouveau texte est vide — sans rien écrire.
 */
export async function modifierCommentaire(
  itemId: string,
  avant: string,
  contenu: string,
  identites: readonly string[],
): Promise<Commentaire> {
  const refus = erreurCorrection(avant, contenu)
  if (refus) throw new Error(refus)
  const chemin = `${listPath()}/items/${itemId}?${ITEM_SELECT}`
  const actuel = mapItemToCommentaire(await graphGet<SharePointListItem<CommentaireFields>>(chemin))
  if (!peutModifierCommentaire(actuel, identites, true)) {
    throw new Error('Seul son auteur peut corriger un commentaire.')
  }
  if (actuel.contenu.trim() !== avant.trim()) {
    throw new Error('Ce commentaire a changé entre-temps : rechargez la fiche, rien n’a été enregistré.')
  }
  await graphPatch<unknown>(`${listPath()}/items/${itemId}/fields`, { field_5: contenu.trim() })
  return mapItemToCommentaire(await graphGet<SharePointListItem<CommentaireFields>>(chemin))
}

/** Une version de ligne telle que Graph la renvoie. */
interface VersionGraph {
  id: string
  lastModifiedDateTime?: string
  fields?: { field_5?: string }
}

/**
 * Les textes successifs d'un commentaire, le plus récent d'abord (23/10), lus
 * dans l'historique des versions de SharePoint. Graph rend les champs de chaque
 * version avec `$expand=fields` ; s'il ne le fait pas, on les demande version
 * par version (au plus 20).
 */
export async function historiqueCommentaire(itemId: string): Promise<VersionCommentaire[]> {
  const base = `${listPath()}/items/${itemId}/versions`
  const reponse = await graphGet<{ value: VersionGraph[] }>(`${base}?$expand=fields`)
  const versions = await Promise.all(
    reponse.value.slice(0, 20).map(async (v): Promise<VersionBrute> => {
      let contenu = v.fields?.field_5
      if (contenu === undefined) {
        try {
          const champs = await graphGet<{ field_5?: string }>(`${base}/${encodeURIComponent(v.id)}/fields`)
          contenu = champs.field_5
        } catch {
          // Version illisible : écartée (versionsDuCommentaire ignore un texte absent).
        }
      }
      return { date: v.lastModifiedDateTime ?? '', ...(contenu !== undefined ? { contenu } : {}) }
    }),
  )
  return versionsDuCommentaire(versions)
}
